<template>
  <v-card class="pa-4">
    <v-card-title class="text-h6 pb-3 mb-3">
      Keywords to track local rank for
    </v-card-title>

    <v-card-text>
      <div v-if="loading" class="flex justify-center items-center h-full">
        <v-progress-circular indeterminate color="primary" :size="32" />
      </div>
      <div v-else>
        <!-- Current Keywords Display as Editable Input Fields -->
        <div v-if="currentKeywords?.length > 0" class="mb-4">
          <!-- <h4 class="mb-3">Current Keywords</h4> -->
          <div class="mb-3">
            <v-row class="max-w-[60%]">
              <v-col
                v-for="(keywordObj, index) in currentKeywords"
                :key="index"
                cols="12"
              >
                <v-text-field
                  v-model="keywordObj.keyword"
                  :label="`Keyword ${index + 1}`"
                  outlined
                  dense
                  :rules="keywordRules"
                  @input="onKeywordEdit(index, $event)"
                >
                  <template v-slot:append>
                    <v-btn icon small color="red" @click="removeKeyword(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
                <div class="text-caption text-gray-600 mt-n5 mb-2">
                  Used in {{ keywordObj.count }} location{{
                    keywordObj.count !== 1 ? "s" : ""
                  }}
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Location Variables Checkbox -->
        <!-- <div class="mb-3">
          <v-checkbox
            v-model="showLocationVariables"
            label="Add location variables"
            dense
            class="mt-0"
          ></v-checkbox>
        </div> -->

        <!-- Add New Keywords -->
        <div>
          <v-text-field
            v-model="newKeyword"
            label="Enter new keyword"
            outlined
            dense
            @keyup.enter="addKeyword"
            :rules="keywordRules"
          >
            <template v-slot:append>
              <v-btn
                icon
                small
                color="primary"
                :disabled="!newKeyword.trim()"
                @click="addKeyword"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <h4 class="mb-2">
            <!-- Add New Keywords ({{ currentKeywords.length }}/5) -->
            <v-chip
              v-if="hasKeywordLimitError"
              small
              color="warning"
              class="ml-2"
            >
              Exceeds Limit
            </v-chip>
          </h4>

          <!-- Location Variables -->
          <div class="mb-3">
            <h5 class="mb-2 text-subtitle-2">Location Variables:</h5>
            <div class="d-flex flex-wrap gap-2">
              <v-chip
                v-for="variable in locationVariables"
                :key="variable.value"
                small
                outlined
                :color="hasLocationVariableInInput ? 'grey' : 'secondary'"
                :disabled="hasLocationVariableInInput"
                clickable
                @click="addLocationVariable(variable)"
                class="mb-2"
                :class="{ 'opacity-50': hasLocationVariableInInput }"
              >
                <!-- <v-icon left small>mdi-map-marker</v-icon> -->
                {{ variable.value }}
              </v-chip>
            </div>
            <p class="text-caption text-gray-600">
              <span v-if="hasLocationVariableInInput">
                Location variable added. Add the keyword or clear the input to
                add a different variable.
              </span>
              <span v-else>
                Click on a location variable to add it to your keyword. Make
                sure to include a keyword before or after the location variable.
              </span>
            </p>
          </div>

          <!-- Keyword Suggestions -->
          <div v-if="showSuggestions" class="mb-3 mt-3">
            <h5 class="mb-2 text-subtitle-2">Suggested Keywords:</h5>
            <div class="d-flex flex-wrap gap-2">
              <v-chip
                v-for="suggestion in suggestedKeywords"
                :key="suggestion"
                small
                outlined
                color="primary"
                class="mb-2"
              >
                {{ suggestion }}
              </v-chip>
            </div>
          </div>

          <v-alert
            v-if="duplicateKeywords.length > 0"
            type="error"
            dense
            class="mt-2"
          >
            Duplicate keywords found: {{ duplicateKeywords.join(", ") }}
          </v-alert>
        </div>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <div class="d-flex flex-column align-end">
        <!-- Save Error Message -->
        <v-alert
          v-if="saveError"
          type="error"
          dense
          class="mb-2"
          style="max-width: 400px"
        >
          {{ saveError }}
        </v-alert>

        <v-btn
          color="primary"
          :loading="saving"
          :disabled="autoSaving"
          @click="saveKeywords"
        >
          <v-icon v-if="autoSaving" left small class="mr-1"
            >mdi-content-save-auto</v-icon
          >
          {{ autoSaving ? "Auto-saving..." : "Save Keywords" }}
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  limit,
  setDoc,
  where,
  getDoc,
} from "firebase/firestore";
import { db } from "@/utils/firebase.utils";
import commonMixin from "@/mixins/common.mixins";
import moment from "moment";

export default {
  name: "GmbKeywordsSetting",
  mixins: [commonMixin],

  data() {
    return {
      currentKeywords: [], // Array of objects: [{keyword: 'string', count: number}]
      originalKeywords: [],
      newKeyword: "",
      loading: false,
      saving: false,
      autoSaving: false, // Track auto-save state
      autoSaveTimer: null, // For debouncing edits
      keywordRules: [
        (v) => !!v || "Keyword is required",
        (v) => (v && v.length >= 2) || "Keyword must be at least 2 characters",
        // (v) =>
        // (v && v.length <= 50) || "Keyword must be less than 50 characters",
        (v) => this.validateKeywordWithVariables(v),
      ],
      totalWorkspaces: 0,
      isAdminKeywords: false,
      showLocationVariables: false,
      locationVariables: [
        {
          label: "Location Short Address",
          value: "{{location-short-address}}",
        },
        { label: "Location City", value: "{{location-city}}" },
      ],
      saveError: "", // Track save error messages
    };
  },

  computed: {
    hasChanges() {
      const currentSorted = JSON.stringify(
        this.currentKeywords.sort((a, b) => a.keyword.localeCompare(b.keyword))
      );
      const originalSorted = JSON.stringify(
        this.originalKeywords.sort((a, b) => a.keyword.localeCompare(b.keyword))
      );
      return currentSorted !== originalSorted;
    },

    duplicateKeywords() {
      const keywords = this.currentKeywords.map((k) =>
        k.keyword.trim().toLowerCase()
      );
      const duplicates = keywords.filter(
        (keyword, index) => keyword && keywords.indexOf(keyword) !== index
      );
      return [...new Set(duplicates)];
    },

    hasDuplicates() {
      return this.duplicateKeywords.length > 0;
    },

    showSuggestions() {
      return this.currentKeywords.length < 5;
    },

    suggestedKeywords() {
      if (!this.showSuggestions) return [];
      return [
        "keyword in {{location-short-address}}",
        "keyword in {{location-city}}",
      ];
    },

    hasKeywordLimitError() {
      return this.currentKeywords.length > 5;
    },

    canSave() {
      return (
        this.currentKeywords.length > 0 &&
        this.currentKeywords.length <= 5 &&
        this.hasChanges &&
        !this.hasDuplicates
      );
    },

    hasLocationVariableInInput() {
      return this.locationVariables.some((locationVar) =>
        this.newKeyword.includes(locationVar.value)
      );
    },
  },

  async mounted() {
    await this.fetchCurrentKeywords();
  },

  methods: {
    validateKeywordWithVariables(value) {
      if (!value) return true; // Let other rules handle empty values

      const trimmedValue = value.trim();
      const hasOnlyVariables = this.locationVariables.every((variable) => {
        const withoutVariable = trimmedValue.replace(variable.value, "").trim();
        return withoutVariable === "" || withoutVariable === "in";
      });

      if (hasOnlyVariables) {
        return "Please add a keyword before or after the location variable";
      }

      return true;
    },

    addLocationVariable(variable) {
      // Don't allow adding if chips are disabled (location variable already exists)
      if (this.hasLocationVariableInInput) {
        return;
      }

      // Add the variable at the end of current input
      this.newKeyword = this.newKeyword.trim() + " " + variable.value;
    },

    async addKeyword() {
      const keyword = this.newKeyword.trim();

      if (!keyword) {
        this.$snackbar.show({
          message: "Please enter a keyword",
          color: "warning",
        });
        return;
      }
      // Check if we're trying to add a location variable without a proper keyword
      const currentInput = this.newKeyword.trim();
      let inputWithoutVariables = currentInput;

      // Remove all existing location variables from the input
      this.locationVariables.forEach((var1) => {
        inputWithoutVariables = inputWithoutVariables
          .replace(var1.value, "")
          .trim();
      });

      // // If after removing location variables, we only have empty string or "in", show warning
      if (inputWithoutVariables === "") {
        this.$snackbar.show({
          message: "Please add a keyword before or after the location variable",
          color: "warning",
        });
        return;
      }

      // Check if keyword already exists (case-insensitive)
      const existingKeyword = this.currentKeywords.find(
        (k) => k.keyword.toLowerCase() === keyword.toLowerCase()
      );
      if (existingKeyword) {
        this.$snackbar.show({
          message: "Keyword already exists",
          color: "warning",
        });
        return;
      }

      // Add new keyword with count of 1 (will be applied to new document)
      this.currentKeywords.push({
        keyword,
        count: 1,
      });
      this.newKeyword = "";

      // Clear save error when keywords are within limit
      if (this.currentKeywords.length <= 5) {
        this.saveError = "";
      }

      // Auto-save after adding keyword
      await this.autoSaveKeywords();
    },

    async removeKeyword(index) {
      this.currentKeywords.splice(index, 1);

      // Clear save error when keywords are within limit
      if (this.currentKeywords.length <= 5) {
        this.saveError = "";
      }

      // Auto-save after removing keyword
      await this.autoSaveKeywords();
    },

    onKeywordEdit(index, newValue) {
      // Update the keyword while preserving the count
      this.currentKeywords[index].keyword = newValue;

      // Debounced auto-save for edits
      this.debouncedAutoSave();
    },

    debouncedAutoSave() {
      // Clear existing timer
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
      }

      // Set new timer for 1 second delay
      this.autoSaveTimer = setTimeout(async () => {
        await this.autoSaveKeywords();
      }, 1000);
    },

    async autoSaveKeywords() {
      // Don't auto-save if there are validation errors
      if (this.hasDuplicates || this.currentKeywords.length > 5) {
        return;
      }

      // Don't auto-save if already saving manually
      if (this.saving) {
        return;
      }

      if (!this.currentUID || !this.WID) {
        console.error("Missing currentUID or WID");
        return;
      }

      this.autoSaving = true;
      try {
        // Calculate next Monday's date
        const nextMonday = this.getNextMondayDate();

        // Create document with next Monday's date as ID
        const keywordDocRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "keywords",
          nextMonday
        );

        // Extract just the keywords (without counts) for saving, and trim whitespace
        const keywordsArray = this.currentKeywords
          .map((k) => k.keyword.trim())
          .filter((k) => k.length > 0); // Remove empty keywords

        const keywordData = {
          keywords: keywordsArray,
          created_at: new Date(),
        };

        await setDoc(keywordDocRef, keywordData);

        // Update original keywords to current state
        this.originalKeywords = JSON.parse(
          JSON.stringify(this.currentKeywords)
        );

        // Show subtle auto-save feedback
        this.$snackbar.show({
          message: "Keywords auto-saved",
          color: "success",
          timeout: 2000,
        });
      } catch (error) {
        console.error("Error auto-saving keywords:", error);
        // Don't show error snackbar for auto-save failures to avoid spam
      } finally {
        this.autoSaving = false;
      }
    },

    async fetchCurrentKeywords() {
      if (!this.currentUID || !this.WID) {
        console.error("Missing currentUID or WID");
        return;
      }
      console.log("fetchCurrentKeywords", this.currentUID, this.WID);

      this.loading = true;
      try {
        // First, get all workspaces where admin == this.WID with pagination if needed
        const workspacesRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces"
        );
        const workspacesQuery = query(
          workspacesRef,
          where("admin", "==", this.WID)
        );
        const workspacesSnapshot = await getDocs(workspacesQuery);

        if (workspacesSnapshot.empty) {
          console.log("No workspaces found");
          this.currentKeywords = [];
          this.originalKeywords = [];
          return;
        }

        const workspaceIds = workspacesSnapshot.docs.map((doc) => doc.id);
        this.totalWorkspaces = workspaceIds.length;
        console.log(`Found ${workspaceIds.length} workspaces to process`);

        const adminKeywords = await this.fetchAdminKeywords();
        if (adminKeywords && adminKeywords.length > 0) {
          console.log("Using admin keywords:", adminKeywords);
          this.isAdminKeywords = true;

          // Convert admin keywords to the expected format with count = totalWorkspaces
          const keywordsWithCounts = adminKeywords.map((keyword) => ({
            keyword,
            count: this.totalWorkspaces,
          }));

          this.currentKeywords = keywordsWithCounts;
          this.originalKeywords = JSON.parse(
            JSON.stringify(keywordsWithCounts)
          );
        } else {
          console.log(
            "No admin keywords found, falling back to workspace keywords"
          );
          this.isAdminKeywords = false;

          // Process workspaces in parallel batches to optimize performance
          const keywordCounts = await this.processWorkspacesInBatches(
            workspaceIds
          );

          // Convert counts object to array of objects
          const keywordsWithCounts = Object.entries(keywordCounts).map(
            ([keyword, count]) => ({
              keyword,
              count,
            })
          );

          console.log("Keywords with counts:", keywordsWithCounts);

          this.currentKeywords = keywordsWithCounts;
          this.originalKeywords = JSON.parse(
            JSON.stringify(keywordsWithCounts)
          );
        }
      } catch (error) {
        console.error("Error fetching keywords:", error);
        this.$snackbar.show({
          message: "Error loading keywords",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminKeywords() {
      try {
        // get the latest keyword document ordered by desc created_at inside keywords collection
        const adminKeywordsDocRef = query(
          collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "keywords"
          ),
          orderBy("created_at", "desc"),
          limit(1)
        );

        const adminKeywordsDoc = await getDocs(adminKeywordsDocRef);

        if (adminKeywordsDoc.empty) {
          return null;
        }

        const adminKeywordsData = adminKeywordsDoc.docs[0].data();

        if (
          adminKeywordsData.keywords &&
          Array.isArray(adminKeywordsData.keywords)
        ) {
          return adminKeywordsData.keywords;
        }

        return null; // No admin keywords found
      } catch (error) {
        console.error("Error fetching admin keywords:", error);
        return null;
      }
    },

    async processWorkspacesInBatches(workspaceIds) {
      const BATCH_SIZE = 8; // Optimal batch size to stay under Firestore's concurrent request limits
      const keywordCounts = {};
      const batches = [];

      // Split workspace IDs into batches
      for (let i = 0; i < workspaceIds.length; i += BATCH_SIZE) {
        batches.push(workspaceIds.slice(i, i + BATCH_SIZE));
      }

      console.log(
        `Processing ${workspaceIds.length} workspaces in ${batches.length} batches of ${BATCH_SIZE}`
      );

      // Process each batch sequentially, but workspace queries within each batch run in parallel
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        console.log(
          `Processing batch ${batchIndex + 1}/${batches.length} with ${
            batch.length
          } workspaces`
        );

        // Create promises for parallel execution within the batch
        const batchPromises = batch.map((workspaceId) =>
          this.fetchKeywordsForWorkspace(workspaceId)
        );

        // Use Promise.allSettled to handle partial failures gracefully
        const batchResults = await Promise.allSettled(batchPromises);

        // Process successful results and log failures
        batchResults.forEach((result, index) => {
          if (result.status === "fulfilled" && result.value) {
            const workspaceKeywords = result.value;
            // Merge keywords counts
            workspaceKeywords.forEach((keyword) => {
              if (keywordCounts[keyword]) {
                keywordCounts[keyword]++;
              } else {
                keywordCounts[keyword] = 1;
              }
            });
          } else if (result.status === "rejected") {
            console.warn(
              `Failed to fetch keywords for workspace ${batch[index]}:`,
              result.reason
            );
          }
        });

        // Add a small delay between batches to be respectful to Firebase
        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      return keywordCounts;
    },

    async fetchKeywordsForWorkspace(workspaceId) {
      try {
        // Access the keywords subcollection for this workspace
        const keywordsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          workspaceId,
          "keywords"
        );

        // Get the most recent keywords document
        const keywordsQuery = query(
          keywordsRef,
          orderBy("created_at", "desc"),
          limit(1)
        );

        const keywordsSnapshot = await getDocs(keywordsQuery);

        if (!keywordsSnapshot.empty) {
          const latestKeywordsDoc = keywordsSnapshot.docs[0];
          const keywordsData = latestKeywordsDoc.data();

          if (keywordsData.keywords && Array.isArray(keywordsData.keywords)) {
            return keywordsData.keywords;
          }
        }

        return []; // Return empty array if no keywords found
      } catch (error) {
        console.error(
          `Error fetching keywords for workspace ${workspaceId}:`,
          error
        );
        throw error; // Re-throw to be handled by Promise.allSettled
      }
    },

    async saveKeywords() {
      if (!this.currentUID || !this.WID) {
        console.error("Missing currentUID or WID");
        return;
      }

      // Check keyword limit
      if (this.currentKeywords.length >= 5 && this.newKeyword.trim() != "") {
        this.saveError = `Maximum 5 keywords allowed. Please remove ${Math.abs(
          5 - this.currentKeywords.length
        )} keyword${
          Math.abs(5 - this.currentKeywords.length) > 1 ? "s" : ""
        } before saving.`;
        return;
      }

      if (this.hasDuplicates) {
        this.saveError = "Please fix duplicate keywords before saving.";
        return;
      }

      // Clear any previous errors
      this.saveError = "";

      this.saving = true;
      try {
        // Calculate next Monday's date
        const nextMonday = this.getNextMondayDate();
        console.log("nextMonday", nextMonday);

        // Create document with next Monday's date as ID
        const keywordDocRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "keywords",
          nextMonday
        );

        // Extract just the keywords (without counts) for saving, and trim whitespace
        const keywordsArray = this.currentKeywords
          .map((k) => k.keyword.trim())
          .filter((k) => k.length > 0); // Remove empty keywords

        const keywordData = {
          keywords: keywordsArray,
          created_at: new Date(),
        };

        await setDoc(keywordDocRef, keywordData);

        // Update original keywords to current state
        this.originalKeywords = JSON.parse(
          JSON.stringify(this.currentKeywords)
        );

        this.$snackbar.show({
          message: `Keywords saved successfully for next run`,
          color: "success",
        });
      } catch (error) {
        console.error("Error saving keywords:", error);
        this.saveError = "Error saving keywords. Please try again.";
      } finally {
        this.saving = false;
      }
    },

    getNextMondayDate() {
      const today = moment();
      const nextMonday = today.clone().add(1, "week").startOf("isoWeek");
      return nextMonday.format("YYYY-MM-DD");
    },
  },

  beforeDestroy() {
    // Clean up timer when component is destroyed
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
